import axios from 'axios';
import { SOURCE_IDENTITY_CHANGED } from 'shared/api/sourceIdentity';
import { config } from 'shared/config';
import type { DataModelResponse, DatabaseSchemaLike } from '../lib/ConnectionModelSupport';

export type ReportSourceSnapshot = NonNullable<DataModelResponse['model']> & {
    connectionId: number;
    selectedSchema?: DatabaseSchemaLike;
};

export interface ConnectionModelSnapshot {
    modelRevision?: number;
    sources?: ReportSourceSnapshot[];
    entities?: Array<Record<string, any>>;
    dataDefinitions?: Array<Record<string, any>>;
    modelFields?: Array<Record<string, any>>;
    relationships?: Array<Record<string, any>>;
    [key: string]: unknown;
}

export interface ConnectionModelPayload {
    status?: string;
    message?: string;
    model?: ConnectionModelSnapshot | null;
}

interface ConnectionModelResourceKey {
    connectionId: number;
    userId: string;
    reportId?: string | null;
}

interface LoadConnectionModelOptions extends ConnectionModelResourceKey {
    force?: boolean;
}

interface CachedConnectionModel {
    expiresAt: number;
    payload: ConnectionModelPayload;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_CACHE_ENTRIES = 24;
const snapshotCache = new Map<string, CachedConnectionModel>();
const inFlightLoads = new Map<string, { version: number; promise: Promise<ConnectionModelPayload> }>();
const invalidationVersionByConnection = new Map<string, number>();
window.addEventListener(SOURCE_IDENTITY_CHANGED, () => {
    snapshotCache.clear(); inFlightLoads.clear();
    invalidationVersionByConnection.forEach((version, key) => invalidationVersionByConnection.set(key, version + 1));
});

const normalizeResourceKey = ({ connectionId, userId, reportId }: ConnectionModelResourceKey): string => (
    `${String(userId || '').trim()}::${String(reportId || '').trim()}`
);

const pruneSnapshotCache = (now: number): void => {
    snapshotCache.forEach((entry, key) => {
        if (entry.expiresAt <= now) {
            snapshotCache.delete(key);
        }
    });
    while (snapshotCache.size >= MAX_CACHE_ENTRIES) {
        const oldestKey = snapshotCache.keys().next().value as string | undefined;
        if (!oldestKey) {
            break;
        }
        snapshotCache.delete(oldestKey);
    }
};

/**
 * Loads the report model through a shared read-through resource. Consumers that
 * mount together reuse the same promise, and subsequent drawer/tab mounts reuse a
 * short-lived immutable snapshot instead of issuing parallel requests.
 */
export const loadConnectionModel = async (
    options: LoadConnectionModelOptions,
): Promise<ConnectionModelPayload> => {
    if (!options.reportId) throw new Error('A report is required to load its data model.');
    const revisionKey = String(options.reportId);
    const key = normalizeResourceKey(options);
    const now = Date.now();
    const resourceVersion = invalidationVersionByConnection.get(revisionKey) || 0;
    invalidationVersionByConnection.set(revisionKey, resourceVersion);
    const cached = snapshotCache.get(key);
    if (!options.force && cached && cached.expiresAt > now) {
        // Refresh insertion order so the capped map behaves as a small LRU.
        snapshotCache.delete(key);
        snapshotCache.set(key, cached);
        return cached.payload;
    }
    if (cached) {
        snapshotCache.delete(key);
    }

    const existingLoad = inFlightLoads.get(key);
    if (existingLoad && existingLoad.version === resourceVersion) {
        return existingLoad.promise;
    }

    const requestPromise = axios.get<ConnectionModelPayload>(
        `${config.API_URL}/api/reports/${encodeURIComponent(options.reportId)}/data-model`,
        {
            params: {
                userId: String(options.userId || '').trim(),
                reportId: options.reportId || undefined,
            },
        },
    ).then((response) => {
        const payload = response.data || {};
        if ((invalidationVersionByConnection.get(revisionKey) || 0) === resourceVersion) {
            pruneSnapshotCache(Date.now());
            snapshotCache.set(key, {
                expiresAt: Date.now() + CACHE_TTL_MS,
                payload,
            });
        }
        return payload;
    }).finally(() => {
        const activeLoad = inFlightLoads.get(key);
        if (activeLoad?.promise === requestPromise) {
            inFlightLoads.delete(key);
        }
    });

    inFlightLoads.set(key, { version: resourceVersion, promise: requestPromise });
    return requestPromise;
};

/** Every source shares the owning report's revision. */
export const invalidateConnectionModel = (connectionId: number, reportId?: string | null): void => {
    if (!reportId) return;
    invalidationVersionByConnection.set(reportId, (invalidationVersionByConnection.get(reportId) || 0) + 1);
    Array.from(snapshotCache.keys()).forEach(key => {
        if (key.endsWith('::' + reportId)) snapshotCache.delete(key);
    });
};
